"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TableColumnsDisplayGrid = exports.columns = void 0;
const tslib_1 = require("tslib");
const react_1 = tslib_1.__importDefault(require("react"));
const catalog_core_1 = require("@tableau/catalog-core");
const catalog_messages_1 = require("@tableau/catalog-messages");
const catalog_queries_1 = require("@tableau/catalog-queries");
const data_catalog_shared_1 = require("@tableau/data-catalog-shared");
const ColumnInlineActionsMenu_1 = require("../ColumnDetails/ColumnInlineActionsMenu");
const NumberCellFormatter_1 = require("../datagrid/cellFormatters/NumberCellFormatter");
const QueryPreservingLinkCellFormatter_1 = require("../datagrid/cellFormatters/QueryPreservingLinkCellFormatter");
const RemoteTypeIcon_1 = require("../datagrid/cellFormatters/RemoteTypeIcon");
const TagCellFormatter_1 = require("../datagrid/cellFormatters/TagCellFormatter");
const LabelsHookHelper_1 = require("../Labels/LabelsHookHelper");
function columns(siteUrls, canUpdate) {
    const cols = [
        () => (0, data_catalog_shared_1.createSelectionColumn)(),
        (sortParams) => ({
            name: 'type',
            dataKey: data_catalog_shared_1.UnsortableColumnKey,
            headerText: catalog_messages_1.Messages.GridHeaderType(),
            headerFormatter: (0, data_catalog_shared_1.createSortableHeaderFormatter)(sortParams),
            cellFormatter: (0, data_catalog_shared_1.createIconCellFormatter)(({ remoteType }) => react_1.default.createElement(RemoteTypeIcon_1.RemoteTypeIcon, { remoteType: remoteType }), ({ remoteType }) => data_catalog_shared_1.RemoteTypeMappings[remoteType]()),
            resizable: false,
            sortable: false,
        }),
        () => createLabelGridColumnFormatter(siteUrls, 'WARNING'),
        (sortParams) => ({
            name: 'name',
            dataKey: catalog_queries_1.ColumnOrderField.NAME,
            headerText: catalog_messages_1.Messages.GridHeaderName(),
            headerFormatter: (0, data_catalog_shared_1.createSortableHeaderFormatter)(sortParams),
            cellFormatter: (0, QueryPreservingLinkCellFormatter_1.createQueryPreservingReplacingLinkCellFormatter)(({ name }) => name || '', (urls, { id, tableId }) => urls.catalogTableColumnPath(tableId, id)),
            resizable: true,
            sortable: true,
        }),
        (sortParams) => ({
            name: 'actionsmenu',
            dataKey: data_catalog_shared_1.UnsortableColumnKey,
            headerText: catalog_messages_1.Messages.Actions(),
            headerFormatter: (0, data_catalog_shared_1.createSortableHeaderFormatter)(sortParams),
            cellFormatter: createColumnActionsCellFormatter({
                getColumnIdFromRowData: (r) => r.__typename === 'Column' ? r.id : '',
                getColumnLuidFromRowData: (r) => r.luid,
                getColumnNameFromRowData: (r) => { var _a; return (_a = r.name) !== null && _a !== void 0 ? _a : ''; },
                getColumnRemoteTypeFromRowData: (r) => r.remoteType,
                getTableVizportalId: (r) => r.vizportalId ? r.vizportalId : '',
                canUpdate,
            }),
            resizable: false,
            sortable: false,
        }),
        (sortParams) => ({
            name: 'connectedSheets',
            dataKey: data_catalog_shared_1.UnsortableColumnKey,
            headerText: catalog_messages_1.Messages.GridHeaderSheets(),
            headerFormatter: (0, data_catalog_shared_1.createSortableHeaderFormatter)(sortParams, 'right'),
            cellFormatter: (0, NumberCellFormatter_1.createNumberCellFormatter)(({ sheetCount }) => sheetCount),
            sortable: false,
        }),
        (sortParams) => ({
            name: 'connectedDatasources',
            dataKey: data_catalog_shared_1.UnsortableColumnKey,
            headerText: catalog_messages_1.Messages.GridHeaderDatasources(),
            headerFormatter: (0, data_catalog_shared_1.createSortableHeaderFormatter)(sortParams, 'right'),
            cellFormatter: (0, NumberCellFormatter_1.createNumberCellFormatter)(({ datasourceCount }) => datasourceCount),
            sortable: false,
        }),
        (sortParams) => ({
            name: 'description',
            dataKey: data_catalog_shared_1.UnsortableColumnKey,
            headerText: catalog_messages_1.Messages.GridHeaderDescription(),
            headerFormatter: (0, data_catalog_shared_1.createSortableHeaderFormatter)(sortParams),
            cellFormatter: (0, data_catalog_shared_1.createTextCellFormatter)(({ description }) => description, catalog_messages_1.Messages.NoDescriptionGridPlaceholder()),
            sortable: false,
            growToFit: true,
        }),
        (sortParams) => ({
            name: 'tags',
            dataKey: data_catalog_shared_1.UnsortableColumnKey,
            headerText: catalog_messages_1.Messages.GridHeaderTags(),
            headerFormatter: (0, data_catalog_shared_1.createSortableHeaderFormatter)(sortParams),
            cellFormatter: (0, TagCellFormatter_1.createTagCellFormatter)(({ tags }) => tags !== null && tags !== void 0 ? tags : []),
            sortable: false,
            growToFit: true,
        }),
        () => createLabelGridColumnFormatter(siteUrls, 'CUSTOM'),
    ];
    return cols.filter(Boolean);
}
exports.columns = columns;
function TableColumnsDisplayGrid(props) {
    const canUpdate = useCanUserSetColumnDescription(props.tableVizportalId);
    const siteUrls = (0, catalog_core_1.useSiteUrls)();
    const cols = columns(siteUrls, canUpdate)
        .map(columnFactory => columnFactory(props));
    return (react_1.default.createElement(data_catalog_shared_1.ExplorerStyledDisplayGrid, { columns: cols, rowStore: props.rowStore, selectedKeys: props.selectedKeys, updateSelectionForKey: props.updateSelectionForKey, rowCount: props.rowCount, renderErrors: data_catalog_shared_1.reportGraphQLErrors, orderBy: props.orderBy, updateSortOrder: props.updateSortOrder }));
}
exports.TableColumnsDisplayGrid = TableColumnsDisplayGrid;
const ActionsCellStyle = {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
};
function useCanUserSetColumnDescription(tableVizportalId) {
    const { canUserSetColumnDescription } = (0, catalog_core_1.useApiRequests)();
    const [canUpdate, setCanUpdate] = react_1.default.useState(false);
    react_1.default.useEffect(() => {
        let subscribed = true;
        const fetchData = () => tslib_1.__awaiter(this, void 0, void 0, function* () {
            const data = yield canUserSetColumnDescription({ tableVizportalId });
            if (subscribed) {
                setCanUpdate(data);
            }
        });
        void fetchData().catch();
        return () => {
            subscribed = false;
        };
    }, [canUserSetColumnDescription, tableVizportalId]);
    return (canUpdate);
}
function createColumnActionsCellFormatter(opts) {
    const { getColumnIdFromRowData, getColumnLuidFromRowData, getColumnNameFromRowData, getColumnRemoteTypeFromRowData, getTableVizportalId, canUpdate, } = opts;
    return (props) => {
        const columnId = getColumnIdFromRowData(props.row.data);
        const tableVizportalId = getTableVizportalId(props.row.data);
        const luid = getColumnLuidFromRowData(props.row.data);
        const name = getColumnNameFromRowData(props.row.data);
        const remoteType = getColumnRemoteTypeFromRowData(props.row.data);
        return (react_1.default.createElement("div", { style: ActionsCellStyle },
            react_1.default.createElement("div", null,
                react_1.default.createElement(ColumnInlineActionsMenu_1.ColumnInlineActionsMenu, { columnId: columnId, columnLuid: luid, columnName: name, columnRemoteType: remoteType, tableVizportalId: tableVizportalId, canUpdate: canUpdate }))));
    };
}
function getLabelContentFromRowData(rowData, urls) {
    var _a;
    return {
        contentId: rowData.id,
        contentLuid: rowData.luid,
        contentType: 'column',
        contentName: (_a = rowData.name) !== null && _a !== void 0 ? _a : undefined,
        contentUrl: urls.catalogTableColumnUrl(rowData.tableId, rowData.id),
        subjectArea: 'Column',
        remoteType: rowData.remoteType,
    };
}
function createLabelGridColumnFormatter(urls, category) {
    function getPropsFromRowData(rowData) {
        const content = getLabelContentFromRowData(rowData, urls);
        const labels = rowData.labels ? rowData.labels : [];
        return (0, LabelsHookHelper_1.getLabelParamsForGrids)({ category, labels, urls, source: 'table-columns', content });
    }
    return (0, data_catalog_shared_1.createLabelColumnFormatter)((0, LabelsHookHelper_1.labelCategoryToString)(category), {
        overrides: {},
        getPropsFromRowData,
    });
}
//# sourceMappingURL=TableColumnsDisplayGrid.js.map